import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ClassroomType } from '@peekapak/types/DataProtocolTypes';
import { AccessibleTeacherItem, CoTeacherMembershipItem, ManagedClassroomSearchItem } from './types';

export enum LoadState {
  idle = 'IDLE',
  loading = 'LOADING',
  loaded = 'LOADED',
  failed = 'FAILED',
}

interface AdminCoTeachersState {
  managedClassrooms: ManagedClassroomSearchItem[];
  managedClassroomsState: LoadState;
  selectedClassroomId: string | null;
  selectedClassroom: ClassroomType | null;
  coTeachers: CoTeacherMembershipItem[];
  classroomDetailsState: LoadState;
  accessibleTeachers: AccessibleTeacherItem[];
  teachersState: LoadState;
  errorMessage: string | null;
}

const initialState: AdminCoTeachersState = {
  managedClassrooms: [],
  managedClassroomsState: LoadState.idle,
  selectedClassroomId: null,
  selectedClassroom: null,
  coTeachers: [],
  classroomDetailsState: LoadState.idle,
  accessibleTeachers: [],
  teachersState: LoadState.idle,
  errorMessage: null,
};

const adminCoTeachersSlice = createSlice({
  name: 'adminCoTeachers',
  initialState,
  reducers: {
    setManagedClassroomsLoading: (state) => {
      state.managedClassroomsState = LoadState.loading;
      state.errorMessage = null;
    },
    setManagedClassrooms: (state, action: PayloadAction<ManagedClassroomSearchItem[]>) => {
      state.managedClassrooms = action.payload;
      state.managedClassroomsState = LoadState.loaded;
    },
    setClassroomDetailsLoading: (state, action: PayloadAction<string>) => {
      state.selectedClassroomId = action.payload;
      state.classroomDetailsState = LoadState.loading;
      state.errorMessage = null;
    },
    setClassroomDetails: (
      state,
      action: PayloadAction<{ classroomId: string; classroom: ClassroomType; coTeachers: CoTeacherMembershipItem[] }>,
    ) => {
      state.selectedClassroomId = action.payload.classroomId;
      state.selectedClassroom = action.payload.classroom;
      state.coTeachers = action.payload.coTeachers;
      state.classroomDetailsState = LoadState.loaded;
    },
    setTeachersLoading: (state) => {
      state.teachersState = LoadState.loading;
      state.errorMessage = null;
    },
    setAccessibleTeachers: (state, action: PayloadAction<AccessibleTeacherItem[]>) => {
      state.accessibleTeachers = action.payload;
      state.teachersState = LoadState.loaded;
    },
    setCoTeachers: (state, action: PayloadAction<CoTeacherMembershipItem[]>) => {
      state.coTeachers = action.payload;
    },
    clearClassroomSelection: (state) => {
      state.selectedClassroomId = null;
      state.selectedClassroom = null;
      state.coTeachers = [];
      state.classroomDetailsState = LoadState.idle;
    },
    clearAdminCoTeachersError: (state) => {
      state.errorMessage = null;
    },
    setAdminCoTeachersError: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
      state.managedClassroomsState = LoadState.failed;
      state.classroomDetailsState = LoadState.failed;
      state.teachersState = LoadState.failed;
    },
  },
});

export const {
  setManagedClassroomsLoading,
  setManagedClassrooms,
  setClassroomDetailsLoading,
  setClassroomDetails,
  setTeachersLoading,
  setAccessibleTeachers,
  setCoTeachers,
  clearClassroomSelection,
  clearAdminCoTeachersError,
  setAdminCoTeachersError,
} = adminCoTeachersSlice.actions;

export default adminCoTeachersSlice.reducer;
